//src/Components/ConfirmContinue/index.tsx
import useMediaQueries from 'app/hooks/useMediaQueries';
import { NotFoundIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import OverlayWindow, {
  PaddingSize,
  WindowSize,
} from 'Components/OverlayWindow';
import { PopupApi, PopupHandles } from 'Components/Popups/Popup/type';
import useCreateModal from 'Components/Popups/useCreateModal';
import { createContext, ReactNode, useState } from 'react';
export const ConfirmContinueContext = createContext<{
  setConfig: (props: ConfirmContinueProps) => void;
  Modal: PopupHandles<any>;
}>({} as any);

export interface ConfirmContinueProps {
  theme?: 'succes' | 'danger';
  title?: string | ReactNode;
  text?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  Icon?: ReactNode;
  onConfirm?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  closePopup?: () => void;
  showDefaultIcon?: boolean;
}

export const ConfirmContinueComponent = ({
  closePopup,
  Icon,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  onConfirm,
  onCancel,
  title,
  text,
  theme = 'succes',
  showDefaultIcon = true,
}: ConfirmContinueProps) => {
  const [isLoading, setIsLoading] = useState(false);
  return (
    <>
      <div className="flex justify-center items-center my-6">
        {showDefaultIcon && !Icon ? (
          theme === 'danger' ? (
            <NotFoundIcon className="w-28 h-28 text-red-light" />
          ) : (
            <NotFoundIcon className="w-28 h-28 text-green-light" />
          )
        ) : (
          <></>
        )}
        {Icon}
      </div>
      {text && (
        <div className="mt-2 flex items-center text-medium text-gray-dark font-semibold mb-2 capitalize">
          {title}
        </div>
      )}
      <div className="text-gray-700 text-sm mt-2">{text}</div>
      <div className="flex justify-center gap-x-3 my-6">
        <div className="flex-1">
          <CustomButton
            textSize="text-sm"
            bgColor="gray-100"
            textColor="gray-600"
            loading={isLoading}
            text={cancelText}
            borderColor="gray-100"
            onClick={() =>
              onCancel
                ? onCancel().then(closePopup)
                : closePopup && closePopup()
            }
          />
        </div>
        <div className="flex-1">
          <CustomButton
            textSize="text-sm"
            loading={isLoading}
            bgColor={theme === 'succes' ? 'green' : 'red-dark'}
            borderColor={theme === 'succes' ? 'green' : 'red-dark'}
            textColor="white"
            text={confirmText}
            onClick={() => {
              setIsLoading(true);
              onConfirm && onConfirm().then(closePopup);
            }}
          />
        </div>
      </div>
    </>
  );
};

export function ConfirmContinueProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<ConfirmContinueProps>();
  const { md_down: isMobile } = useMediaQueries();
  const ConfirmContinueModal = useCreateModal();

  return (
    <ConfirmContinueContext.Provider
      value={{ setConfig, Modal: ConfirmContinueModal }}
    >
      {children}
      <ConfirmContinueModal.Component
        placement={isMobile ? 'stick-to-bottom' : 'screen-centered'}
      >
        <OverlayWindow
          size={WindowSize.md}
          paddingSize={isMobile ? 'px-6 rounded-3xl' : PaddingSize.md}
          onClose={ConfirmContinueModal.close}
          rounded={isMobile ? 'rounded-3xl rounded-b-none' : 'rounded-xl'}
        >
          <ConfirmContinueComponent
            {...config}
            closePopup={ConfirmContinueModal.close}
          />
        </OverlayWindow>
      </ConfirmContinueModal.Component>
    </ConfirmContinueContext.Provider>
  );
}
